import React, { useState, useEffect, useRef } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import Navbar from "../Layout/Navbar/navbar";
import Sidebar from "../Layout/Sidebar/sidebar";
import styles from "./layout.module.css";
import API from "../Services/api";
import {
  loadHome,
  loadOrders,
} from "../routeLoaders";

const Layout = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const contentAreaRef = useRef(null);

  useEffect(() => {
    if (contentAreaRef.current) {
      contentAreaRef.current.scrollTo({ top: 0, left: 0, behavior: "instant" });
      contentAreaRef.current.scrollTop = 0;
    }
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;

    // Real-time wallet & profile sync on route navigation (throttled to avoid redundant bursts)
    const token = localStorage.getItem("magizh_token");
    const lastSync = parseInt(sessionStorage.getItem("last_me_sync") || "0", 10);
    const now = Date.now();
    if (token && (now - lastSync > 30000)) {
      sessionStorage.setItem("last_me_sync", String(now));
      API.get("/me")
        .then(({ data }) => {
          if (data?.status && data?.data) {
            localStorage.setItem("magizh_user", JSON.stringify(data.data));
            window.dispatchEvent(new Event("magizh-user-updated"));
          }
        })
        .catch(() => {});
    }
  }, [location.pathname, location.search]);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  const getGuideUrl = () => {
    if (process.env.REACT_APP_GUIDE_URL) {
      return process.env.REACT_APP_GUIDE_URL;
    }
    const isLocal =
      typeof window !== "undefined" &&
      (window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1");

    if (isLocal) {
      return "http://localhost:3001";
    }

    const hostname = window.location.hostname;
    if (hostname.includes("magizhone.in")) {
      return "https://guide.magizhone.in";
    }

    return `https://guide.${hostname.replace(/^www\./, "")}`;
  };

  const bottomNavItems = [
    {
      id: "home",
      label: "Home",
      path: "/home",
      activePaths: ["/", "/home"],
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
        >
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
          <polyline points="9 22 9 12 15 12 15 22" />
        </svg>
      ),
    },
    {
      id: "prime",
      label: "Prime",
      path: "/prime-member",
      activePaths: ["/prime-member"],
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M2 4l3 12h14l3-12-6 7-4-7-4 7-6-7z" />
        </svg>
      ),
    },
    {
      id: "purchase",
      label: "Purchase",
      path: "/orders",
      activePaths: ["/orders", "/purchase"],
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
        >
          <path d="M6 8V6a6 6 0 0 1 12 0v2" />
          <path d="M4 8h16l1 13H3L4 8Z" />
        </svg>
      ),
    },
    {
      id: "guide",
      label: "Guide",
      path: "/guide",
      isExternal: true,
      activePaths: [],
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <polygon points="10 8 16 12 10 16 10 8" fill="currentColor" stroke="none" />
        </svg>
      ),
    },
  ];

  const matchedIndex = bottomNavItems.findIndex((item) =>
    item.activePaths?.includes(location.pathname)
  );
  const activeBottomIndex = matchedIndex !== -1 ? matchedIndex : 0;

  const handleNavClick = (item) => {
    if (item.isExternal) {
      const url = getGuideUrl();
      const win = window.open(url, "_blank", "noopener,noreferrer");
      if (!win) {
        window.location.href = url;
      }
      return;
    }
    navigate(item.path);
  };

  const routeLoaders = {
    "/home": loadHome,
    "/prime-member": () => {},
    "/orders": loadOrders,
  };
  return (
    <div className={styles.appWrapper}>
      <Navbar onToggleSidebar={toggleSidebar} />

      <div className={styles.mainContainer}>
        <Sidebar
          isOpen={isSidebarOpen}
          onClose={closeSidebar}
        />

        <main className={styles.contentArea} ref={contentAreaRef}>
          <Outlet />
        </main>
      </div>

      {/* Mobile Bottom Navigation */}
     <nav
  className={styles.bottomNav}
  style={{ "--active-index": activeBottomIndex }}
>
  {bottomNavItems.map((item, index) => {
    const isActive =
      item.activePaths.includes(location.pathname) ||
      (matchedIndex === 0 && index === 0 && (location.pathname === "/" || location.pathname === "/home"));

    return (
      <button
        key={item.id}
        type="button"
        className={`${styles.bottomNavItem} ${
          isActive ? styles.bottomActive : ""
        }`}
        onPointerEnter={routeLoaders[item.path]}
        onFocus={routeLoaders[item.path]}
        onTouchStart={routeLoaders[item.path]}
        onClick={() => handleNavClick(item)}
      >
        <span className={styles.bottomIcon}>
          {item.icon}
        </span>

        <span className={styles.bottomLabel}>
          {item.label}
        </span>
      </button>
    );
  })}
</nav>
    </div>
  );
};

export default Layout;